
import React from 'react';
import { Product } from '@/lib/supabase/products';
import { Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface ProductsTableProps {
  products: Product[];
  selectedProducts: string[];
  toggleProductSelection: (id: string) => void;
  handleEditProduct: (id: string) => void;
  handleDeleteProduct: (id: string) => void;
  formatDate: (dateString: string) => string;
  toggleSelectAll: () => void;
  areAllSelected: boolean;
}

const ProductsTable = ({
  products,
  selectedProducts,
  toggleProductSelection,
  handleEditProduct,
  handleDeleteProduct,
  formatDate,
  toggleSelectAll,
  areAllSelected
}: ProductsTableProps) => {
  return (
    <div className="reference-table-container">
      <Table>
        <TableHeader className="reference-table-header">
          <TableRow>
            <TableHead className="w-[50px] reference-table-cell-header">
              <Checkbox 
                checked={areAllSelected}
                onCheckedChange={toggleSelectAll}
                aria-label="Sélectionner tous les produits"
              />
            </TableHead>
            <TableHead className="reference-table-cell-header">Produit</TableHead>
            <TableHead className="reference-table-cell-header">Créé</TableHead>
            <TableHead className="reference-table-cell-header">Stock</TableHead>
            <TableHead className="reference-table-cell-header">Prix</TableHead>
            <TableHead className="w-[80px] text-right reference-table-cell-header">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-gray-500 dark:text-gray-400">
                Aucun produit trouvé
              </TableCell>
            </TableRow>
          ) : (
            products.map((product) => (
              <TableRow 
                key={product.id} 
                className={selectedProducts.includes(product.id) 
                  ? 'reference-table-row-selected' 
                  : 'reference-table-row'
                }
              >
                <TableCell>
                  <Checkbox 
                    checked={selectedProducts.includes(product.id)}
                    onCheckedChange={() => toggleProductSelection(product.id)}
                    aria-label={`Sélectionner ${product.name}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    {product.media_urls && product.media_urls.length > 0 ? (
                      <div className="flex-shrink-0 h-10 w-10 mr-4">
                        <img className="h-10 w-10 rounded-md object-cover" src={product.media_urls[0]} alt={product.name} />
                      </div>
                    ) : (
                      <div className="flex-shrink-0 h-10 w-10 mr-4 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-gray-500 dark:text-gray-400 text-xs">N/A</span>
                      </div>
                    )}
                    <div className="font-medium text-gray-900 dark:text-gray-200">{product.name}</div>
                  </div>
                </TableCell>
                <TableCell className="reference-table-cell">
                  {formatDate(product.created_at)}
                </TableCell>
                <TableCell className="reference-table-cell">
                  {product.stock_quantity}
                </TableCell>
                <TableCell className="reference-table-cell">
                  {product.discount_price && product.discount_price > 0 
                    ? product.discount_price.toLocaleString('fr-FR')
                    : product.price.toLocaleString('fr-FR')
                  } {product.currency || 'DZD'}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 dark:text-gray-300 dark:hover:bg-gray-800"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="data-table-dropdown">
                        <DropdownMenuLabel className="dark:text-gray-300">Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator className="data-table-dropdown-separator" />
                        <DropdownMenuItem 
                          onClick={() => handleEditProduct(product.id)}
                          className="data-table-dropdown-item"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600 dark:text-red-400 data-table-dropdown-item"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProductsTable;
